import { CreateClassificationSchema, UpdateClassificationSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class ClassificationService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Sınıflandırmalar getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch classification list');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch classification');
        }
    }

    async create(data) {
        const validation = CreateClassificationSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Geçersiz form verisi', validation.error.format());
        }

        try {
            const response = await this.api.create(validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Sınıflandırma oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create classification');
        }
    }

    async update(id, data) {
        const validation = UpdateClassificationSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Geçersiz form verisi', validation.error.format());
        }

        try {
            const response = await this.api.update(id, validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Sınıflandırma güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update classification');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Sınıflandırma silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete classification');
        }
    }

    async bulkDelete(ids) {
        try {
            if (this.api.batchDelete) {
                const response = await this.api.batchDelete(ids);
                return {
                    success: true,
                    data: response.data,
                    message: response.message || `${ids.length} sınıflandırma silindi`
                };
            }

            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} sınıflandırma silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete classification items');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
